import { computed, inject, Service, signal } from '@angular/core';
import { UserSettingEventConsumerService } from '../../../events/user-setting/user-setting-event-consumer.service';
import { GeneralSettingsService } from '../../../services/general-settings.service';

/**
 * The کیف پول tab's view-only state, held above the components that read it.
 *
 * Two things outlive a single component:
 *
 * - `defaultWalletKey`, کیف پیش‌فرض. Read from the event bus rather than fetched,
 *   so the cached record already picks the right wallet on the first paint of a
 *   session instead of showing گواهی سپرده and then switching.
 * - `amountsHidden`, the card's eye control. It has to survive leaving the tab
 *   and coming back, which a signal inside the card could not.
 *
 * Deliberately separate from `WalletStore`: this is display preference, that is
 * wallet money.
 */
@Service()
export class WalletViewStateService {
	private readonly userSetting = inject(UserSettingEventConsumerService);
	private readonly generalSettings = inject(GeneralSettingsService);

	/**
	 * کیف پیش‌فرض — the wallet the card shows. گواهی سپرده until the
	 * «تغییر کیف پیش‌فرض» story stores another; see `WalletSettings`.
	 */
	readonly defaultWalletKey = computed(() => this.userSetting.getSetting().wallet.defaultWalletKey);

	/**
	 * The card's own eye control, or `null` while it has not been touched this
	 * session — in which case «عدم نمایش مانده» decides, as it does for every
	 * other balance the app prints.
	 *
	 * A nullable override rather than a plain boolean so the eye can still reveal
	 * a card that the stored setting opened masked. A boolean initialised from the
	 * setting would have the button toggle a value the setting immediately wins
	 * back, which is not a control at all.
	 */
	private readonly amountsHiddenOverride = signal<boolean | null>(null);

	/** True while the card's figures are masked. */
	readonly amountsHidden = computed(() => this.amountsHiddenOverride() ?? this.generalSettings.hideBalances());

	/**
	 * Flip the card's figures. Not persisted: this story only owes the design its
	 * entry point, and «تنظیمات کیف پول» is where a stored choice would belong.
	 */
	toggleAmounts(): void {
		this.amountsHiddenOverride.set(!this.amountsHidden());
	}
}
